/**
 * TSV Elite Filters v4 — Dark Editorial Premium
 *
 * - Dark overlay panels with capsule chip layout
 * - Gold accent sliding tab indicator
 * - Inline search wired to chip flow
 * - AJAX section rendering (debounced, AbortController)
 * - Keyboard arrow-key nav
 * - Clear All uses collection URL
 */

(function () {
  "use strict";

  const forms = document.querySelectorAll("form.cf");
  if (!forms.length) return;

  const norm = (s) => String(s ?? "").trim().toLowerCase();

  const parseQueryFromUrl = (url) => {
    try { return new URL(url, location.origin).searchParams; }
    catch { return new URLSearchParams((url.split("?")[1] || "").split("#")[0]); }
  };

  const buildUrlFromForm = (form) => {
    const base = form.getAttribute("action") || location.pathname;
    const fd = new FormData(form);
    for (const [k, v] of [...fd.entries()]) {
      if (v === "" || v == null) fd.delete(k);
    }
    const qs = new URLSearchParams(fd).toString();
    return qs ? `${base}?${qs}` : base;
  };

  const buildSectionUrl = (form, pageUrl) => {
    const sid = form.dataset.sectionId;
    if (!sid) return pageUrl;
    const url = new URL(pageUrl, location.origin);
    url.searchParams.set("section_id", sid);
    return url.toString();
  };

  /* ═══════════════════════════════════════════
     SYNC INPUTS ↔ URL
  ═══════════════════════════════════════════ */
  const syncInputsToUrl = (form, url) => {
    const params = parseQueryFromUrl(url);

    form.querySelectorAll('input[type="checkbox"][name]').forEach((cb) => {
      cb.checked = params.getAll(cb.name).includes(cb.value);
    });

    form.querySelectorAll("[data-price-hidden-min],[data-price-hidden-max]").forEach((inp) => {
      if (!inp.name) return;
      inp.value = params.get(inp.name) || "";
    });

    form.querySelectorAll("[data-price-bands]").forEach((wrap) => {
      const hMin = wrap.querySelector("[data-price-hidden-min]");
      const hMax = wrap.querySelector("[data-price-hidden-max]");
      const curMin = hMin?.value ?? "";
      const curMax = hMax?.value ?? "";

      wrap.querySelectorAll('input[type="radio"][data-band]').forEach((r) => {
        r.checked =
          String(curMin) === String(r.getAttribute("data-min") || "") &&
          String(curMax) === String(r.getAttribute("data-max") || "");
      });
    });
  };

  /* ═══════════════════════════════════════════
     ACTIVE BAR
  ═══════════════════════════════════════════ */
  const clearActiveBarUI = (form) => {
    const bar = form.querySelector("[data-cf-active]");
    if (!bar) return;
    const row = bar.querySelector(".cf__activeRow");
    if (row) row.innerHTML = "";
    bar.removeAttribute("data-cf-visible");
  };

  const swapActiveBar = (form, sectionRoot) => {
    const current = form.querySelector("[data-cf-active]");
    const next = sectionRoot.querySelector("form.cf [data-cf-active]");
    if (!current) return;
    if (!next) { clearActiveBarUI(form); return; }

    current.replaceWith(next);
    const replaced = form.querySelector("[data-cf-active]");
    if (!replaced) return;

    if (replaced.querySelector(".cf__pill")) {
      replaced.setAttribute("data-cf-visible", "true");
    } else {
      replaced.removeAttribute("data-cf-visible");
    }
  };

  /* ═══════════════════════════════════════════
     PANEL CLEAR BUTTONS
  ═══════════════════════════════════════════ */
  const updatePanelClearButtons = (form) => {
    form.querySelectorAll("[data-cf-panel]").forEach((panel) => {
      const isPrice = !!panel.querySelector("[data-price-bands]");
      let hasAny = false;

      if (isPrice) {
        const wrap = panel.querySelector("[data-price-bands]");
        const hMin = wrap?.querySelector("[data-price-hidden-min]");
        const hMax = wrap?.querySelector("[data-price-hidden-max]");
        hasAny = !!((hMin?.value) || (hMax?.value));
      } else {
        hasAny = panel.querySelectorAll('input[type="checkbox"]:checked').length > 0;
      }

      let clearBtn = panel.querySelector(".cf__clearOne");

      if (hasAny && !clearBtn) {
        const actions = panel.querySelector(".cf__panelActions");
        if (!actions) return;
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "cf__clearOne";
        btn.textContent = "Clear";
        btn.setAttribute(isPrice ? "data-reset-price" : "data-reset-filter", "");
        const close = actions.querySelector("[data-cf-close]");
        close ? actions.insertBefore(btn, close) : actions.prepend(btn);
      }

      if (!hasAny && clearBtn) clearBtn.remove();
    });
  };

  /* ═══════════════════════════════════════════
     SEARCH — filters chips by label text
  ═══════════════════════════════════════════ */
  const filterPanelChips = (panel, query) => {
    const q = norm(query);
    panel.querySelectorAll(".cf__chip").forEach((chip) => {
      if (!q) { chip.style.display = ""; return; }
      const label = chip.querySelector(".cf__chipLabel");
      chip.style.display = norm(label?.textContent ?? chip.textContent).includes(q) ? "" : "none";
    });
  };

  /* ═══════════════════════════════════════════
     TAB INDICATOR — thin gold underline
  ═══════════════════════════════════════════ */
  const moveIndicator = (form, trigger) => {
    const indicator = form.querySelector(".cf__tabIndicator");
    if (!indicator) return;
    if (!trigger) { indicator.removeAttribute("data-cf-visible"); return; }

    const tabs = form.querySelector(".cf__tabs");
    if (!tabs) return;

    const tabsRect = tabs.getBoundingClientRect();
    const trigRect = trigger.getBoundingClientRect();
    const left = trigRect.left - tabsRect.left + tabs.scrollLeft;
    const width = trigRect.width;

    if (indicator.getAttribute("data-cf-visible") === "true") {
      indicator.animate(
        [
          { left: indicator.style.left, width: indicator.style.width },
          { left: `${left}px`, width: `${width}px` },
        ],
        { duration: 350, easing: "cubic-bezier(.22,1,.36,1)", fill: "forwards" }
      );
    }

    indicator.style.left = `${left}px`;
    indicator.style.width = `${width}px`;
    indicator.setAttribute("data-cf-visible", "true");
  };

  const hideIndicator = (form) => {
    const indicator = form.querySelector(".cf__tabIndicator");
    if (indicator) indicator.removeAttribute("data-cf-visible");
  };

  /* ═══════════════════════════════════════════
     OPEN / CLOSE
  ═══════════════════════════════════════════ */
  const closeAll = (form) => {
    form.querySelectorAll("[data-cf-trigger]").forEach((btn) => {
      btn.setAttribute("aria-selected", "false");
      btn.setAttribute("aria-expanded", "false");
    });
    form.querySelectorAll("[data-cf-panel]").forEach((panel) => {
      panel.setAttribute("data-cf-open", "false");
    });
    hideIndicator(form);
    // Restore filtered chips
    form.querySelectorAll("[data-cf-panel] .cf__chip").forEach((c) => { c.style.display = ""; });
  };

  const openIndex = (form, idx) => {
    const trigger = form.querySelector(`[data-cf-trigger][data-filter-index="${idx}"]`);
    const panel = form.querySelector(`[data-cf-panel][data-filter-index="${idx}"]`);
    if (!trigger || !panel) return;

    const wasOpen = trigger.getAttribute("aria-selected") === "true";
    closeAll(form);
    if (wasOpen) return;

    trigger.setAttribute("aria-selected", "true");
    trigger.setAttribute("aria-expanded", "true");
    panel.setAttribute("data-cf-open", "true");

    updatePanelClearButtons(form);
    moveIndicator(form, trigger);

    // Re-apply search
    const search = panel.querySelector("[data-cf-search]");
    if (search?.value) filterPanelChips(panel, search.value);

    try { trigger.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" }); } catch {}
  };

  /* ═══════════════════════════════════════════
     AJAX
  ═══════════════════════════════════════════ */
  const createRequestManager = () => {
    let controller = null;
    let running = false;
    let queuedUrl = null;
    let counter = 0;

    return (form, url) => {
      queuedUrl = url;
      counter++;
      if (running) return Promise.resolve();
      running = true;

      const process = () => {
        if (!queuedUrl) { running = false; return Promise.resolve(); }
        const nextUrl = queuedUrl;
        queuedUrl = null;
        const requestId = counter;

        const grid = document.querySelector("#ProductGridContainer");
        if (!grid) { location.href = nextUrl; return Promise.resolve(); }

        const sectionUrl = buildSectionUrl(form, nextUrl);
        controller?.abort();
        controller = new AbortController();
        grid.classList.add("is-loading");

        return fetch(sectionUrl, {
          method: "GET",
          headers: { "X-Requested-With": "XMLHttpRequest" },
          signal: controller.signal,
        })
          .then((res) => { if (!res.ok) { location.href = nextUrl; return; } return res.text(); })
          .then((html) => {
            if (!html) return;
            grid.classList.remove("is-loading");
            if (requestId !== counter && queuedUrl) return process();

            const doc = new DOMParser().parseFromString(html, "text/html");
            const root = doc.querySelector(".shopify-section") || doc;
            const newGrid = root.querySelector("#ProductGridContainer");
            if (!newGrid) { location.href = nextUrl; return; }

            grid.innerHTML = newGrid.innerHTML;
            swapActiveBar(form, root);
            history.pushState({}, "", nextUrl);
            syncInputsToUrl(form, nextUrl);
            updatePanelClearButtons(form);
            updateTabBadges(form, root);

            if (queuedUrl) return process();
          })
          .catch((err) => {
            grid.classList.remove("is-loading");
            if (err?.name === "AbortError") return queuedUrl ? process() : undefined;
            location.href = nextUrl;
          })
          .finally(() => { running = false; });
      };
      return process();
    };
  };

  const updateTabBadges = (form, sectionRoot) => {
    const newForm = sectionRoot.querySelector("form.cf");
    if (!newForm) return;
    form.querySelectorAll("[data-cf-trigger]").forEach((trigger) => {
      const idx = trigger.getAttribute("data-filter-index");
      const newTrigger = newForm.querySelector(`[data-cf-trigger][data-filter-index="${idx}"]`);
      if (!newTrigger) return;
      trigger.querySelector(".cf__triggerBadge")?.remove();
      const newBadge = newTrigger.querySelector(".cf__triggerBadge");
      if (newBadge) trigger.appendChild(newBadge.cloneNode(true));
    });
  };

  /* ═══════════════════════════════════════════
     INIT
  ═══════════════════════════════════════════ */
  forms.forEach((form) => {
    const request = createRequestManager();
    const cfRequest = (url) => request(form, url);
    let debounceTimer = null;
    const debouncedRequest = (url) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => cfRequest(url), 150);
    };

    /* Search inputs */
    form.querySelectorAll("[data-cf-search]").forEach((input) => {
      const panel = input.closest("[data-cf-panel]");
      if (!panel) return;
      input.addEventListener("input", () => filterPanelChips(panel, input.value));
    });

    /* Click delegation */
    form.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-cf-trigger]");
      if (trigger) { e.preventDefault(); e.stopPropagation(); openIndex(form, trigger.dataset.filterIndex); return; }

      if (e.target.closest("[data-cf-close]")) { e.preventDefault(); closeAll(form); return; }

      const clearAllBtn = e.target.closest(".cf__clearAll");
      if (clearAllBtn) {
        e.preventDefault();
        form.querySelectorAll('input[type="checkbox"]').forEach((i) => { i.checked = false; });
        form.querySelectorAll("[data-price-hidden-min],[data-price-hidden-max]").forEach((i) => { i.value = ""; });
        form.querySelectorAll('input[type="radio"][data-band]').forEach((r) => { r.checked = false; });
        updatePanelClearButtons(form);
        closeAll(form);
        clearActiveBarUI(form);
        cfRequest(form.getAttribute("data-collection-url") || form.getAttribute("action") || location.pathname);
        return;
      }

      const pill = e.target.closest(".cf__pill");
      if (pill) { e.preventDefault(); const url = pill.getAttribute("href"); if (url) cfRequest(url); return; }

      const resetFilter = e.target.closest("[data-reset-filter]");
      if (resetFilter) {
        e.preventDefault();
        const panel = resetFilter.closest("[data-cf-panel]");
        if (!panel) return;
        panel.querySelectorAll('input[type="checkbox"]').forEach((i) => { i.checked = false; });
        const search = panel.querySelector("[data-cf-search]");
        if (search) filterPanelChips(panel, search.value);
        updatePanelClearButtons(form);
        cfRequest(buildUrlFromForm(form));
        return;
      }

      const resetPrice = e.target.closest("[data-reset-price]");
      if (resetPrice) {
        e.preventDefault();
        const panel = resetPrice.closest("[data-cf-panel]");
        if (!panel) return;
        const wrap = panel.querySelector("[data-price-bands]");
        if (wrap) {
          const hMin = wrap.querySelector("[data-price-hidden-min]");
          const hMax = wrap.querySelector("[data-price-hidden-max]");
          if (hMin) hMin.value = "";
          if (hMax) hMax.value = "";
          wrap.querySelectorAll('input[type="radio"][data-band]').forEach((r) => { r.checked = false; });
        }
        updatePanelClearButtons(form);
        cfRequest(buildUrlFromForm(form));
        return;
      }
    });

    /* Change delegation */
    form.addEventListener("change", (e) => {
      const checkbox = e.target.closest('input[type="checkbox"]');
      if (checkbox) { updatePanelClearButtons(form); debouncedRequest(buildUrlFromForm(form)); return; }

      const radio = e.target.closest('input[type="radio"][data-band]');
      if (radio?.checked) {
        const wrap = radio.closest("[data-price-bands]");
        if (!wrap) return;
        const hMin = wrap.querySelector("[data-price-hidden-min]");
        const hMax = wrap.querySelector("[data-price-hidden-max]");
        if (!hMin || !hMax) return;
        const minAttr = radio.getAttribute("data-min");
        const maxAttr = radio.getAttribute("data-max");
        hMin.value = minAttr == null || minAttr === "" ? "" : String(Number(minAttr));
        hMax.value = maxAttr == null || maxAttr === "" ? "" : String(Number(maxAttr));
        updatePanelClearButtons(form);
        debouncedRequest(buildUrlFromForm(form));
      }
    });

    /* Click outside */
    document.addEventListener("click", (e) => {
      if (e.target.closest("form.cf") !== form) closeAll(form);
    });

    /* Keyboard */
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { closeAll(form); return; }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const active = form.querySelector('[data-cf-trigger][aria-selected="true"]');
        if (!active) return;
        const triggers = [...form.querySelectorAll("[data-cf-trigger]")];
        const idx = triggers.indexOf(active);
        if (idx === -1) return;
        const next = e.key === "ArrowRight" ? (idx + 1) % triggers.length : (idx - 1 + triggers.length) % triggers.length;
        e.preventDefault();
        openIndex(form, triggers[next].dataset.filterIndex);
        triggers[next].focus();
      }
    });

    /* Popstate */
    window.addEventListener("popstate", () => { cfRequest(location.href); updatePanelClearButtons(form); });

    /* Init */
    updatePanelClearButtons(form);
    syncInputsToUrl(form, location.href);
    const activeBar = form.querySelector("[data-cf-active]");
    if (activeBar) {
      if (activeBar.querySelector(".cf__pill")) activeBar.setAttribute("data-cf-visible", "true");
      else activeBar.removeAttribute("data-cf-visible");
    }
  });
})();